import { FaHeart, FaUserAlt } from "react-icons/fa";
import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { useAuth } from "../../context/auth";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import {
  FiHeart,
  FiShoppingBag,
  FiTrendingUp,
  FiDollarSign,
  FiPackage,
  FiXCircle,
} from "react-icons/fi";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Sidebar from "./UserSidebar";

const PALETTE = {
  DARK_NAVY: "#1C2E4A",
  ACCENT_BROWN: "#7C4F2B",
  LIGHT_CREAM: "#F8F6F3",
  GOLD: "#D4AF37",
};

const Dashboard = () => {
  const [auth, setAuth] = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("statistics");

  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [stats, setStats] = useState({
    wishlisted: 0,
    purchased: 0,
    amountSpent: 0,
  });

  const fetchOrders = async () => {
    try {
      const { data } = await axios.get("/api/v1/auth/orders");

      const ordersArray = Array.isArray(data) ? data : data.orders || [];
      setOrders(ordersArray);

      const deliveredOrders = ordersArray.filter(
        (order) => order.status?.toLowerCase() === "delivered"
      );

      const totalSpent = deliveredOrders.reduce((sum, order) => {
        const orderTotal =
          order.products?.reduce((prodSum, item) => {
            return prodSum + (item.product?.price * item.quantity || 0);
          }, 0) || 0;
        return sum + orderTotal;
      }, 0);

      setStats((prev) => ({
        ...prev,
        purchased: ordersArray.length,
        amountSpent: totalSpent,
      }));
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to load orders");
    }
  };

  const fetchWishlist = async () => {
    try {
      const { data } = await axios.get("/api/v1/wishlist");
      const wishlistArray = data.wishlist || [];
      setWishlist(wishlistArray);
      setStats((prev) => ({
        ...prev,
        wishlisted: wishlistArray.length,
      }));
    } catch (error) {
      console.error("Error fetching wishlist:", error);
      toast.error("Failed to load wishlist");
    }
  };

  const toggleWishlist = async (productId) => {
    try {
      const { data } = await axios.delete(`/api/v1/wishlist/${productId}`);
      fetchWishlist();
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancelOrder = async (orderId, orderStatus) => {
    if (orderStatus !== "Not Processed" && orderStatus !== "Processing") {
      toast.error(
        `Cannot cancel order with status: ${orderStatus}. Only orders with status 'Not Processed' or 'Processing' can be cancelled.`
      );
      return;
    }

    const result = await Swal.fire({
      title: "Cancel Order?",
      text: "Are you sure you want to cancel this order? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Cancel Order",
      cancelButtonText: "Keep Order",
      input: "textarea",
      inputPlaceholder: "Reason for cancellation (optional)",
      inputAttributes: {
        "aria-label": "Reason for cancellation",
      },
      showLoaderOnConfirm: true,
      preConfirm: async (reason) => {
        try {
          setCancellingOrder(orderId);
          const { data } = await axios.put(
            `/api/v1/product/order/cancel/${orderId}`,
            {
              reason: reason || "Cancelled by user",
            }
          );
          return data;
        } catch (error) {
          Swal.showValidationMessage(
            `Request failed: ${error.response?.data?.message || error.message}`
          );
        }
      },
      allowOutsideClick: () => !Swal.isLoading(),
    });

    if (result.isConfirmed) {
      toast.success("Order cancelled successfully!");
      await fetchOrders();
      setCancellingOrder(null);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchOrders(), fetchWishlist()]);
      setLoading(false);
    };

    if (auth?.token) {
      fetchData();
    }
  }, [auth?.token]);

  const generateChartData = () => {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const currentMonth = new Date().getMonth();
    const last6Months = [];

    for (let i = 5; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      last6Months.push(monthNames[monthIndex]);
    }

    return last6Months.map((month, index) => {
      const monthIndex = (currentMonth - 5 + index + 12) % 12;
      const monthOrders = orders.filter((order) => {
        const orderMonth = new Date(order.createdAt).getMonth();
        return orderMonth === monthIndex;
      });

      return {
        month,
        Purchases: monthOrders.length,
        Wishlist: index + 2,
      };
    });
  };

  const calculateOrderTotal = (order) => {
    return (
      order.products?.reduce((sum, item) => {
        return sum + (item.product?.price * item.quantity || 0);
      }, 0) || 0
    );
  };

  const formatCurrency = (amount) =>
    amount.toLocaleString("en-IN", { style: "currency", currency: "INR" });

  const handleLogout = () => {
    setAuth({ user: null, token: "" });
    localStorage.removeItem("auth");
    navigate("/login");
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const canCancelOrder = (status) => {
    return status === "Not Processed" || status === "Processing";
  };

  const StatCard = ({ icon: Icon, title, value, trend, color }) => (
    <div className="group relative bg-white rounded-lg p-6 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100">
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${color}15` }}
          >
            <Icon size={24} style={{ color }} />
          </div>
          {trend && (
            <div className="flex items-center gap-1 text-green-600 text-sm font-semibold">
              <FiTrendingUp size={16} />
              {trend}
            </div>
          )}
        </div>
        <h3 className="text-gray-600 text-sm font-medium mb-1">{title}</h3>
        <p className="text-3xl font-bold" style={{ color: PALETTE.DARK_NAVY }}>
          {value}
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Layout title="Dashboard - Russh Berry">
        <div
          className="min-h-screen flex items-center justify-center"
          style={{ backgroundColor: PALETTE.LIGHT_CREAM }}
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-amber-700 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg">Loading dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Dashboard - Russh Berry">
      <div
        className="min-h-screen mt-20"
        style={{ backgroundColor: PALETTE.LIGHT_CREAM }}
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1
                className="text-3xl lg:text-4xl font-bold"
                style={{ color: PALETTE.DARK_NAVY }}
              >
                Welcome back, {auth?.user?.name?.split(" ")[0]}!
              </h1>
              <p className="text-gray-600 mt-2">
                Here's what's happening with your account
              </p>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <aside className="w-full lg:w-72 flex-shrink-0">
              <Sidebar
                auth={auth}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                onLogout={handleLogout}
              />
            </aside>

            <main className="flex-1 min-w-0">
              {activeTab === "statistics" && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <StatCard
                      icon={FiHeart}
                      title="Wishlist Items"
                      value={stats.wishlisted}
                      color={PALETTE.ACCENT_BROWN}
                    />
                    <StatCard
                      icon={FiShoppingBag}
                      title="Total Orders"
                      value={stats.purchased}
                      color={PALETTE.DARK_NAVY}
                    />
                    <StatCard
                      icon={FiDollarSign}
                      title="Total Spent (Delivered)"
                      value={formatCurrency(stats.amountSpent)}
                      color={PALETTE.GOLD}
                    />
                  </div>

                  <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
                    <h3
                      className="text-xl font-bold mb-6"
                      style={{ color: PALETTE.DARK_NAVY }}
                    >
                      Monthly Activity (Last 6 Months)
                    </h3>
                    <ResponsiveContainer width="100%" height={350}>
                      <ComposedChart data={generateChartData()}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fill: "#666" }} />
                        <YAxis tick={{ fill: "#666" }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "white",
                            border: "none",
                            borderRadius: "12px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar
                          dataKey="Purchases"
                          fill={PALETTE.ACCENT_BROWN}
                          radius={[8, 8, 0, 0]}
                          barSize={40}
                          name="Orders Placed"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
                    <h3
                      className="text-xl font-bold mb-6"
                      style={{ color: PALETTE.DARK_NAVY }}
                    >
                      Recent Orders
                    </h3>
                    {orders.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                                Order ID
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                                Products
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                                Status
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                                Amount
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                                Date
                              </th>
                              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.slice(0, 5).map((order, index) => (
                              <tr
                                key={order._id}
                                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                              >
                                <td className="py-4 px-4 text-sm font-medium text-gray-900">
                                  #{index + 1}
                                </td>
                                <td
                                  className="py-4 px-4 text-sm text-gray-700 cursor-pointer hover:text-blue-600"
                                  onClick={() => {
                                    if (order.products?.[0]?.product?.slug) {
                                      navigate(
                                        `/product/${order.products[0].product.slug}`
                                      );
                                    }
                                  }}
                                >
                                  {order.products?.[0]?.product?.name || "N/A"}
                                </td>

                                <td className="py-4 px-4">
                                  <span
                                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                      order.status?.toLowerCase() ===
                                      "delivered"
                                        ? "bg-green-100 text-green-700"
                                        : order.status?.toLowerCase() ===
                                          "shipped"
                                        ? "bg-blue-100 text-blue-700"
                                        : order.status?.toLowerCase() ===
                                          "processing"
                                        ? "bg-yellow-100 text-yellow-700"
                                        : order.status?.toLowerCase() ===
                                          "cancelled"
                                        ? "bg-red-100 text-red-700"
                                        : "bg-gray-100 text-gray-700"
                                    }`}
                                  >
                                    {order.status}
                                  </span>
                                </td>
                                <td
                                  className="py-4 px-4 text-sm font-semibold"
                                  style={{ color: PALETTE.ACCENT_BROWN }}
                                >
                                  {formatCurrency(calculateOrderTotal(order))}
                                </td>
                                <td className="py-4 px-4 text-sm text-gray-600">
                                  {new Date(order.createdAt).toLocaleDateString(
                                    "en-IN",
                                    {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    }
                                  )}
                                </td>
                                <td className="py-4 px-4">
                                  {canCancelOrder(order.status) && (
                                    <button
                                      onClick={() =>
                                        handleCancelOrder(
                                          order._id,
                                          order.status
                                        )
                                      }
                                      disabled={cancellingOrder === order._id}
                                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {cancellingOrder === order._id ? (
                                        <>
                                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                                          <span>Cancelling...</span>
                                        </>
                                      ) : (
                                        <>
                                          <FiXCircle size={14} />
                                          <span>Cancel</span>
                                        </>
                                      )}
                                    </button>
                                  )}
                                  {!canCancelOrder(order.status) && (
                                    <span className="text-xs text-gray-400">
                                      N/A
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FiPackage
                          size={64}
                          className="mx-auto text-gray-400 mb-4"
                        />
                        <p className="text-gray-500 text-lg">No orders yet</p>
                        <button
                          onClick={() => navigate("/products")}
                          className="mt-4 px-6 py-3 rounded-xl font-semibold text-white transition-all"
                          style={{ backgroundColor: PALETTE.ACCENT_BROWN }}
                        >
                          Start Shopping
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "profile" && (
                <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-100">
                  <h2
                    className="text-3xl font-bold mb-8"
                    style={{ color: PALETTE.DARK_NAVY }}
                  >
                    Profile Information
                  </h2>
                  <div className="space-y-6">
                    <div className="flex items-center gap-6 pb-6 border-b border-gray-200">
                      <div className="w-16 h-16 rounded-full bg-gray-300 flex items-center justify-center text-white text-4xl">
                        <FaUserAlt />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">
                          {auth?.user?.name}
                        </h3>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-600 mb-1">
                          Email Address
                        </p>
                        <p className="font-semibold text-gray-900">
                          {auth?.user?.email}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-600 mb-1">
                          Phone Number
                        </p>
                        <p className="font-semibold text-gray-900">
                          {auth?.user?.phone || "Not provided"}
                        </p>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-xl md:col-span-2">
                        <p className="text-sm text-gray-600 mb-1">Address</p>
                        <p className="font-semibold text-gray-900">
                          {auth?.user?.address || "Not provided"}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate("/dashboard/user/profile")}
                      className="mt-6 bg-gray-900 px-8 py-3 rounded-lg font-semibold text-white transition-all duration-200 hover:shadow-lg"
                    >
                      Edit Profile
                    </button>
                  </div>
                </div>
              )}

              {activeTab === "orders" && (
                <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-100">
                  <h2
                    className="text-3xl font-bold mb-8"
                    style={{ color: PALETTE.DARK_NAVY }}
                  >
                    Order History
                  </h2>
                  {orders.length > 0 ? (
                    <div className="space-y-4">
                      {orders.map((order, index) => (
                        <div
                          key={order._id}
                          className="p-6 border border-gray-200 rounded-xl hover:shadow-lg transition-shadow"
                        >
                          <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
                            <div>
                              <p className="font-bold text-lg text-gray-900">
                                Order #{index + 1}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                {new Date(order.createdAt).toLocaleDateString(
                                  "en-IN",
                                  {
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                  }
                                )}
                              </p>
                            </div>
                            <div className="text-right">
                              <p
                                className="text-2xl font-bold mb-2"
                                style={{ color: PALETTE.ACCENT_BROWN }}
                              >
                                {formatCurrency(calculateOrderTotal(order))}
                              </p>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`inline-block px-4 py-1 rounded-full text-sm font-semibold ${
                                    order.status?.toLowerCase() === "delivered"
                                      ? "bg-green-100 text-green-700"
                                      : order.status?.toLowerCase() ===
                                        "shipped"
                                      ? "bg-blue-100 text-blue-700"
                                      : order.status?.toLowerCase() ===
                                        "cancelled"
                                      ? "bg-red-100 text-red-700"
                                      : "bg-yellow-100 text-yellow-700"
                                  }`}
                                >
                                  {order.status}
                                </span>
                                {canCancelOrder(order.status) && (
                                  <button
                                    onClick={() =>
                                      handleCancelOrder(order._id, order.status)
                                    }
                                    disabled={cancellingOrder === order._id}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition disabled:opacity-50"
                                  >
                                    {cancellingOrder === order._id ? (
                                      <>
                                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                                        <span>Cancelling...</span>
                                      </>
                                    ) : (
                                      <>
                                        <FiXCircle size={14} />
                                        <span>Cancel Order</span>
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="border-t pt-4">
                            <p className="text-sm font-semibold text-gray-700 mb-3">
                              Order Items:
                            </p>
                            <div className="space-y-2">
                              {order.products?.map((item) => (
                                <div
                                  key={item._id}
                                  className="flex items-center gap-4 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded transition"
                                  onClick={() => {
                                    if (item.product?.slug) {
                                      navigate(`/product/${item.product.slug}`);
                                    }
                                  }}
                                >
                                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                                    <img
                                      src={`/${item.product?.photos?.[0]}`}
                                      alt={item.product?.name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.src =
                                          "https://via.placeholder.com/64";
                                      }}
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-medium text-gray-900">
                                      {item.product?.name}
                                    </p>
                                    <p className="text-gray-600">
                                      Qty: {item.quantity}
                                    </p>
                                  </div>
                                  <p
                                    className="font-semibold"
                                    style={{ color: PALETTE.ACCENT_BROWN }}
                                  >
                                    {formatCurrency(
                                      item.product?.price * item.quantity
                                    )}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FiPackage
                        size={64}
                        className="mx-auto text-gray-400 mb-4"
                      />
                      <p className="text-gray-500 text-lg">No orders yet</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "wishlist" && (
                <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-100">
                  <h2
                    className="text-3xl font-bold mb-8"
                    style={{ color: PALETTE.DARK_NAVY }}
                  >
                    My Wishlist ({stats.wishlisted} items)
                  </h2>
                  {wishlist.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {wishlist.map((item) => (
                        <div
                          key={item._id}
                          className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow group"
                        >
                          <div className="relative h-56 bg-gray-100">
                            <img
                              src={`/${item.photos?.[0]}`}
                              alt={item.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.src =
                                  "https://via.placeholder.com/300";
                              }}
                            />
                          </div>
                          <div className="p-4">
                            <h4 className="font-semibold text-gray-900 mb-2 line-clamp-1">
                              {item.name}
                            </h4>
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                              {item.description}
                            </p>
                            <div className="flex items-center justify-between">
                              <p
                                className="text-xl font-bold"
                                style={{ color: PALETTE.ACCENT_BROWN }}
                              >
                                {formatCurrency(item.price)}
                              </p>
                              <button
                                onClick={() => toggleWishlist(item._id)}
                                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                              >
                                <FaHeart className="text-red-500 text-xl" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FiHeart
                        size={64}
                        className="mx-auto text-gray-400 mb-4"
                      />
                      <p className="text-gray-500 text-lg mb-4">
                        Your wishlist is empty
                      </p>
                      <button
                        onClick={() => navigate("/products")}
                        className="px-6 py-3 rounded-xl font-semibold text-white transition-all"
                        style={{ backgroundColor: PALETTE.ACCENT_BROWN }}
                      >
                        Browse Products
                      </button>
                    </div>
                  )}
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
